// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventCallback = (...args: any[]) => any;

class EventHub {
  private readonly handlers: { [eventName: string]: Map<unknown, EventCallback> };

  constructor() {
    this.handlers = Object.create(null);
  }

  /**
   * Add a handler for an event.
   * @param eventName
   * @param callback
   */
  on(eventName: string, callback: EventCallback, id?: string) {
    if (eventName in this.handlers) {
      this.handlers[eventName].set(id ?? callback, callback);
    } else {
      this.handlers[eventName] = new Map();
      this.handlers[eventName].set(id ?? callback, callback);
    }
  }
  /**
   * Add a handler for an event. That handler will be removed after being called once.
   * @param eventName
   * @param callback
   */
  once(eventName: string, callback: EventCallback, id?: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const callAndRemove = (...args: any[]) => {
      const result = callback(...args);
      this.off(eventName, callAndRemove);
      return result;
    };
    this.on(eventName, callAndRemove, id);
  }

  /**
   * Remove an event handler
   * @param eventName
   * @param callback
   */
  off(eventName: string, callback: EventCallback, id?: string) {
    if (eventName in this.handlers) {
      if (id) {
        return this.handlers[eventName].delete(id);
      }
      return this.handlers[eventName].delete(callback);
    }
    return false;
  }

  /**
   * Trigger an event.
   * @param eventName
   * @param args
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emit(eventName: string, ...args: any[]) {
    const handlers = this.handlers[eventName] ?? [];
    for (const handler of handlers.values()) {
      handler(...args);
    }
  }

  /**
   * Invoke all event handlers for an event.
   * If any handler returns FALSE, we abort.
   * @param eventName
   * @param args
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  invoke(eventName: string, ...args: any[]) {
    const handlers = this.handlers[eventName] ?? [];
    for (const handler of handlers.values()) {
      if (handler(...args) === false) {
        return false;
      }
    }
    return true;
  }
}
export const eventHub = new EventHub();
export default eventHub;
